import { useState, useEffect, useCallback } from 'react';
import { importesService } from '../services/importesService';
import ConfirmDialog from './ConfirmDialog';
import { useConfirm } from '../hooks/useConfirm';
import { useTiposIva } from '../hooks/useTiposIva';

function fmt(valor) {
  if (valor == null) return '—';
  return Number(valor).toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' €';
}

export default function ImportesPanel({ idSiniestro, soloLectura = false }) {
  const { tiposIva, ivaDefecto }      = useTiposIva();
  const [lista, setLista]             = useState([]);
  const [totalSiniestro, setTotal]    = useState(0);
  const [isLoading, setIsLoading]     = useState(false);
  const [form, setForm]               = useState({ importe: '', iva: ivaDefecto, total: '' });
  const [editandoId, setEditandoId]   = useState(null);
  const [guardando, setGuardando]     = useState(false);
  const [eliminando, setEliminando]   = useState(null);
  const [error, setError]             = useState(null);
  const { confirm, pedirConfirm }     = useConfirm();

  const cargar = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await importesService.porSiniestro(idSiniestro);
      setLista(r.data.importes);
      setTotal(r.data.total);
    } catch {
      setError('Error al cargar importes');
    } finally {
      setIsLoading(false);
    }
  }, [idSiniestro]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => {
      const nuevo = { ...f, [name]: value };
      const imp = parseFloat(nuevo.importe) || 0;
      const iva = parseFloat(nuevo.iva) || 0;
      nuevo.total = (imp + imp * iva / 100).toFixed(2);
      return nuevo;
    });
  };

  const handleNuevo = () => {
    setEditandoId('nuevo');
    setForm({ importe: '', iva: ivaDefecto, total: '' });
    setError(null);
  };

  const handleEditar = (imp) => {
    setEditandoId(imp.id);
    setForm({
      importe: imp.importe != null ? String(imp.importe) : '',
      iva:     imp.iva     != null ? Number(imp.iva)     : ivaDefecto,
      total:   imp.total   != null ? String(imp.total)   : '',
    });
    setError(null);
  };

  const handleCancelar = () => {
    setEditandoId(null);
    setForm({ importe: '', iva: ivaDefecto, total: '' });
    setError(null);
  };

  const handleGuardar = async () => {
    if (!form.importe) { setError('El importe es obligatorio'); return; }
    setGuardando(true);
    try {
      const payload = {
        importe: parseFloat(form.importe),
        iva:     parseFloat(form.iva),
        total:   parseFloat(form.total),
      };
      if (editandoId === 'nuevo') {
        await importesService.crear(idSiniestro, payload);
      } else {
        await importesService.actualizar(editandoId, payload);
      }
      handleCancelar();
      cargar();
    } catch {
      setError('Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    const ok = await pedirConfirm({
      titulo: 'Eliminar importe',
      mensaje: '¿Eliminar este importe? Esta acción no se puede deshacer.',
      peligro: true,
      textOk: 'Eliminar',
    });
    if (!ok) return;
    setEliminando(id);
    try {
      await importesService.eliminar(id);
      cargar();
    } catch {
      setError('Error al eliminar');
    } finally {
      setEliminando(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
          Importes
          {lista.length > 0 && (
            <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-normal normal-case">
              {lista.length}
            </span>
          )}
        </h3>
        {editandoId === null && !soloLectura && (
          <button onClick={handleNuevo}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
            </svg>
            Añadir
          </button>
        )}
      </div>

      {error && <p role="alert" className="text-xs text-red-600">{error}</p>}

      {/* Formulario inline */}
      {editandoId !== null && !soloLectura && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Importe €</label>
              <input
                type="number"
                name="importe"
                value={form.importe}
                onChange={handleChange}
                step="0.01"
                placeholder="0.00"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">IVA %</label>
              <select
                name="iva"
                value={form.iva}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {tiposIva.map(t => (
                  <option key={t.id} value={t.porcentaje}>{t.porcentaje}%</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Total €</label>
              <input
                type="number"
                name="total"
                value={form.total}
                readOnly
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-gray-100 text-gray-600"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={handleGuardar} disabled={guardando}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold py-1.5 rounded-lg transition disabled:opacity-50">
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
            <button onClick={handleCancelar}
              className="flex-1 border border-gray-300 text-gray-600 text-xs font-medium py-1.5 rounded-lg hover:bg-gray-50 transition">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      {isLoading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"/>
        </div>
      ) : lista.length === 0 && editandoId === null ? (
        <p className="text-xs text-gray-400 text-center py-3">Sin importes registrados</p>
      ) : lista.length > 0 && (
        <div className="space-y-1">
          {lista.map(imp => (
            <div key={imp.id}
              className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 group">
              <div className="flex gap-4 text-sm">
                <span className="text-gray-700 font-medium">{fmt(imp.importe)}</span>
                <span className="text-gray-400">IVA {imp.iva}%</span>
                <span className="text-gray-900 font-semibold">{fmt(imp.total)}</span>
              </div>
              {!soloLectura && (
              <div className="flex gap-1 shrink-0 ml-2">
                <button onClick={() => handleEditar(imp)} title="Editar"
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                  </svg>
                </button>
                <button onClick={() => handleEliminar(imp.id)} title="Eliminar"
                  aria-label="Eliminar importe"
                  disabled={eliminando === imp.id}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-40">
                  {eliminando === imp.id
                    ? <div className="w-3.5 h-3.5 animate-spin rounded-full border-2 border-red-400 border-t-transparent"/>
                    : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                  }
                </button>
              </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Total */}
      {!isLoading && lista.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between items-center">
          <span className="text-sm font-semibold text-gray-600">Total siniestro</span>
          <span className="text-lg font-bold text-blue-700">{fmt(totalSiniestro)}</span>
        </div>
      )}

      <ConfirmDialog {...confirm} abierto={confirm !== null} />
    </div>
  );
}
